import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  useTheme,
  Box,
} from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useThemeContext } from '../../Context/ThemeContext';
import GoogleReCAPTCHA from 'react-google-recaptcha';
import PropTypes from 'prop-types';

const VerifyCaptchaDialog = ({
  open,
  onClose,
  onVerified,
  title,
  description,
}) => {
  const theme = useTheme();
  const { darkMode } = useThemeContext();

  const [captchaToken, setCaptchaToken] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const recaptchaRef = useRef(null);

  const borderBase = 'rgba(79, 172, 254, 0.1)';

  const SITE_KEY =
    process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    if (!open) return;
    setCaptchaToken(null);
    setLoadErr('');
    const t = setTimeout(() => {
      try {
        const hasWidget =
          typeof recaptchaRef.current?.getWidgetId === 'function' &&
          recaptchaRef.current.getWidgetId() !== undefined;
        if (hasWidget && typeof recaptchaRef.current?.reset === 'function') {
          recaptchaRef.current.reset();
        }
      } catch (e) {
        // ignore
      }
    }, 200);
    return () => clearTimeout(t);
  }, [open]);

  const handleContinue = () => {
    if (!captchaToken) return;
    onVerified?.();
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      keepMounted
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 1,
          width: 520,
          overflow: 'hidden',
          backgroundColor: darkMode ? '#1e1f22' : '#ffffff',
          border: `1px solid ${borderBase}`,
          boxShadow: darkMode
            ? '0 24px 64px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.04)'
            : '0 24px 64px rgba(0,0,0,0.15), inset 0 1px 2px rgba(0,0,0,0.06)',
          backdropFilter: 'blur(4px)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: darkMode
            ? 'rgba(10, 14, 20, 0.55)'
            : 'rgba(10, 18, 30, 0.35)',
          backdropFilter: 'blur(4px)',
          '& .MuiDialog-container': { alignItems: 'center' },
        },
      }}
      transitionDuration={{ appear: 250, enter: 250, exit: 200 }}
    >
      <DialogContent
        sx={{
          py: 5,
          px: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            mb: 1,
            background: darkMode ? '#2c2c2e' : '#f7fbff',
            border: `1px solid ${borderBase}`,
          }}
        >
          <ShieldOutlinedIcon sx={{ fontSize: '2rem', color: '#5965A6' }} />
        </Box>

        <Typography
          sx={{
            fontSize: '0.95rem',
            fontWeight: 700,
            mb: 0.5,
            color: theme.palette.text.primary,
          }}
        >
          {title || 'Verify it’s you'}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            fontSize: '0.95rem',
            color: theme.palette.text.secondary,
            mb: 1,
          }}
        >
          {description ||
            'Complete the verification to continue editing this connection.'}
        </Typography>

        <Box sx={{ mt: 1 }}>
          {SITE_KEY ? (
            <>
              <GoogleReCAPTCHA
                ref={recaptchaRef}
                sitekey={SITE_KEY}
                theme={darkMode ? 'dark' : 'light'}
                onChange={(token) => {
                  setLoadErr('');
                  setCaptchaToken(token);
                }}
                onExpired={() => setCaptchaToken(null)}
                onErrored={() => {
                  setCaptchaToken(null);
                  setLoadErr(
                    'reCAPTCHA failed to load. Check ad-blockers/CSP or your site key domain settings.',
                  );
                }}
                useRecaptchaNet
                scriptProps={{ async: true, defer: true, appendTo: 'body' }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.75,
                  color: theme.palette.text.secondary,
                }}
              >
                This quick check protects your account from automated actions.
              </Typography>
              {loadErr && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.75, display: 'block' }}
                >
                  {loadErr}
                </Typography>
              )}
            </>
          ) : (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: 'block', mt: 0.75 }}
            >
              reCAPTCHA is not configured. Please set REACT_APP_RECAPTCHA_SITE_KEY environment variable.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: '8px 24px' }}>
        <Button
          onClick={onClose}
          variant="text"
          sx={{
            minWidth: 120,
            fontSize: '0.875rem',
            color: theme.palette.text.secondary,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleContinue}
          variant="contained"
          disabled={!captchaToken}
          sx={{
            minWidth: 160,
            height: 40,
            borderRadius: 1,
            px: 4,
            fontWeight: 700,
            textTransform: 'none',
            fontSize: '0.95rem',
            backgroundImage: 'linear-gradient(90deg, #5965A6, #8E98CD)',
            color: '#fff',
            '&:hover': {
              backgroundImage: 'linear-gradient(90deg, #4C5B9A, #7F8AC4)',
              boxShadow: '0 8px 20px rgba(89,101,166,0.25)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

VerifyCaptchaDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onVerified: PropTypes.func.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
};

export default VerifyCaptchaDialog;
